import React from "react";
import i18n from "@/i18n";
import { componentFromStack, reportClientIncident } from "@/modules/shared/services/clientIncidents";

// Last line of defence for a screen that throws while rendering. The crash is reported to
// the backend (and from there to Tarh Portal) and the user gets a way back instead of a
// blank page. A navigation resets it, so one broken screen does not take the app down.
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, href: typeof window !== "undefined" ? window.location.href : "" };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    reportClientIncident(error, {
      kind: "render",
      component: componentFromStack(info?.componentStack),
    });
  }

  componentDidUpdate() {
    // The user moved on (back button, bottom nav): show the new screen, not the old crash.
    if (this.state.error && typeof window !== "undefined" && window.location.href !== this.state.href) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null, href: window.location.href });
    }
  }

  reload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  home = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.error) return this.props.children;

    const t = (key, fallback) => {
      const value = i18n.t(key);
      return value && value !== key ? value : fallback;
    };
    const dir = i18n.language === "ar" ? "rtl" : "ltr";

    return (
      <main
        dir={dir}
        className="min-h-screen grid place-items-center bg-slate-50 p-6 text-slate-800"
        role="alert"
      >
        <section className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-red-50 text-red-600">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold">{t("app_crashed_title", "Something went wrong on this screen")}</h1>
          <p className="mt-2 text-sm text-slate-600">
            {t("app_crashed_body", "The problem has been reported to our team. Reload the page to continue.")}
          </p>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={this.reload}
              className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-hover"
            >
              {t("reload_page", "Reload page")}
            </button>
            <button
              type="button"
              onClick={this.home}
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t("go_to_home", "Go to home")}
            </button>
          </div>
        </section>
      </main>
    );
  }
}
